import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

let scriptPromise = null;
function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error('Could not load Google sign-in')); };
      document.body.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Google's own "Sign in with Google" button. Renders nothing until the backend has a
 * Google client ID configured. */
export default function GoogleSignIn({ text = 'continue_with', onError }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const { theme } = useTheme();
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    api.get('/api/auth/google-config').then(({ data }) => setClientId(data.client_id || '')).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGoogleScript().then(() => {
      if (cancelled || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            const user = await loginWithGoogle(credential);
            navigate(['admin', 'super_admin'].includes(user?.role) ? '/admin' : '/dashboard');
          } catch (err) {
            onError?.(formatError(err.response?.data?.detail) || 'Google sign-in failed. Please try again.');
          }
        },
      });
      ref.current.innerHTML = '';
      window.google.accounts.id.renderButton(ref.current, {
        theme: theme === 'dark' ? 'filled_black' : 'outline',
        size: 'large', shape: 'pill', text, width: ref.current.offsetWidth || 320,
      });
    }).catch(() => onError?.('Could not load Google sign-in. Check your internet.'));
    return () => { cancelled = true; };
  }, [clientId, theme, text, loginWithGoogle, navigate, onError]);

  if (!clientId) return null;
  return (
    <div className="mt-5" data-testid="google-signin">
      <div className="flex items-center gap-3 mb-4 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
      </div>
      <div ref={ref} className="flex justify-center w-full" />
    </div>
  );
}
